import React, { useState, useEffect } from 'react';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
import { getFranchisees, destroyFranchisee, getFranchiseeUsers } from '../../../api/milma_admin/FranchiseeAPI'; 
// --- Added getBaseUrl ---
import { handleApiError, getBaseUrl } from '../../../api/risapi';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const FranchiseesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Franchisee');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(10);
    
    const [franchiseeList, setFranchiseeList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    const [showUserModal, setShowUserModal] = useState(false);
    const [selectedFranchisee, setSelectedFranchisee] = useState(null);
    const [franchiseeUsers, setFranchiseeUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const handleAddClick = () => navigate('/milma-admin/franchisees/create');

    useEffect(() => {
        if (successMessage) {
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            setShowToaster(true);
            window.history.replaceState({}, document.title);
        }
    }, [successMessage]);

    useEffect(() => {
        setViewDataLoading(true);
        const filterParameters = {
            paginate: true,
            page: currentPage,
            per_page: perPage,
            search_term: searchTermCompleted,
        };

        getFranchisees(filterParameters, (response) => {
            setViewDataLoading(false);
            if (response.franchisees) {
                setFranchiseeList(response.franchisees);
            } else {
                handleApiError(response);
            }
        });
    }, [currentPage, searchTermCompleted, perPage, saveCompleted]);

    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleSearchSubmit = (e) => { e.preventDefault(); setSearchTermCompleted(searchTerm); setCurrentPage(1); };
    const handleClearSearch = () => { setSearchTerm(''); setSearchTermCompleted(''); setCurrentPage(1); };

    const handleViewUsers = (franchisee) => {
        setSelectedFranchisee(franchisee);
        setShowUserModal(true);
        setLoadingUsers(true);
        setFranchiseeUsers([]);

        getFranchiseeUsers(franchisee.id, (response) => {
            setLoadingUsers(false);
            if (response.users) {
                setFranchiseeUsers(response.users);
            } else {
                setToaster({ type: 'danger', heading: 'Error', message: 'Could not load users.' });
                setShowToaster(true);
            }
        });
    };

    const handleDeleteClick = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this! If this franchisee has transactions, it cannot be deleted.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyFranchisee(id, (response) => {
                    if (response && response.error) {
                        const apiError = response.error;
                        const errorMessage = apiError?.response?.data?.message || 'Could not delete franchisee.';
                        Swal.fire({ title: 'Delete Failed!', text: errorMessage, icon: 'error', confirmButtonColor: '#3085d6' });
                    } else {
                        Swal.fire('Deleted!', 'Franchisee and setup data has been deleted.', 'success');
                        setSaveCompleted(prev => !prev); 
                    }
                });
            }
        });
    };

    return (
        <>
            <AppBreadCrumb entityName="Franchisees" breadCrumbType="list" />
            <main className="page-content">
                <div className="container-fluid">
                    {showToaster && <AppToaster toaster={toaster} setShowToaster={setShowToaster} />}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5>Franchisee Registry</h5>
                                    <button className="btn btn-red btn-sm" onClick={handleAddClick}>
                                        <i className="ti ti-plus"></i> Register Franchisee
                                    </button>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <PerPageBlock perPage={perPage} setPerPage={setPerPage} setCurrentPage={setCurrentPage} />
                                        <AppSearchBox searchTerm={searchTerm} handleSearchChange={handleSearchChange} handleSearchSubmit={handleSearchSubmit} handleClearSearch={handleClearSearch} />
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-hover table-bordered mb-0 table-primary">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Region</th>
                                                    <th>PAN</th>
                                                    <th>GSTIN</th>
                                                    <th>Location</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {franchiseeList.data.map((item, index) => (
                                                    <tr key={index}>
                                                        {/* --- NEW: Logo Rendering Logic --- */}
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                {item.logo_path ? (
                                                                    <img 
                                                                        src={`${getBaseUrl()}/storage/${item.logo_path}`} 
                                                                        alt="logo" 
                                                                        className="rounded-circle me-3 border shadow-sm" 
                                                                        style={{width: '40px', height: '40px', objectFit: 'contain', backgroundColor: '#fff'}} 
                                                                    />
                                                                ) : (
                                                                    <div 
                                                                        className="rounded-circle bg-light-primary text-primary d-flex justify-content-center align-items-center me-3 border" 
                                                                        style={{width: '40px', height: '40px', fontWeight: 'bold'}}
                                                                    >
                                                                        {item.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <strong>{item.name}</strong>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-light-primary text-primary">
                                                                {item.region?.name || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>{item.pan}</td>
                                                        <td>{item.gstin || 'N/A'}</td>
                                                        <td>{item.district?.name || 'N/A'}, {item.state?.name || 'N/A'}</td>
                                                        <td className="text-end">
                                                            <button className="btn btn-sm btn-outline-info px-2 py-1 me-2" title="View Users" onClick={() => handleViewUsers(item)}> 
                                                                <i className="ti ti-users f-s-14"></i> Users
                                                            </button>

                                                            <button className="btn btn-sm btn-outline-danger px-2 py-1" title="Delete Franchisee" onClick={() => handleDeleteClick(item.id)}> 
                                                                <i className="ti ti-trash f-s-14"></i> Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {viewDataLoading && ( <tr><td colSpan="6" className="text-center">Loading...</td></tr> )}
                                                {!viewDataLoading && franchiseeList.data.length === 0 && ( <tr><td colSpan="6" className="text-center text-muted">No franchisees found</td></tr> )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="d-flex flex-wrap mt-3 border-top pt-3">
                                        {franchiseeList.data.length > 0 && (
                                            <AppPaginator entityName={entityName} currentPage={franchiseeList.current_page} totalPages={franchiseeList.tot_page} setCurrentPage={setCurrentPage} />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {showUserModal && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show d-block" tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Users for {selectedFranchisee?.name}</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowUserModal(false)}></button>
                                </div>
                                <div className="modal-body p-0">
                                    {loadingUsers ? (
                                        <div className="text-center p-4">Loading users...</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="ps-4">Name</th>
                                                        <th>Phone Number</th>
                                                        <th>Roles</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {franchiseeUsers.length > 0 ? (
                                                        franchiseeUsers.map((u, i) => (
                                                            <tr key={i}>
                                                                <td className="ps-4">
                                                                    <strong>{u.name}</strong>
                                                                    {u.is_milma_admin ? <span className="badge bg-light-danger text-danger ms-2">Milma Admin</span> : null}
                                                                </td>
                                                                <td>{u.phone_number}</td>
                                                                <td>
                                                                    {u.roles && u.roles.length > 0 
                                                                        ? u.roles.map(r => <span key={r.id} className="badge bg-light-secondary text-secondary me-1">{r.name}</span>)
                                                                        : <span className="text-muted">No roles</span>
                                                                    }
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="3" className="text-center text-muted p-4">No users assigned to this franchisee yet.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowUserModal(false)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default FranchiseesPage;